/**
 * FeedbackControls — human-in-the-loop correction buttons. Captures the
 * proprietary correction signal (wrong match, wrong/missing topic, irrelevant
 * recommendation) that improves identity resolution + classification over time.
 */
import { useState } from 'react';
import type { FeedbackType } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

interface FeedbackAction {
  type: FeedbackType;
  label: string;
}

const ACTIONS: FeedbackAction[] = [
  { type: 'match_confirmed', label: 'Confirm match' },
  { type: 'match_wrong', label: 'Wrong match' },
  { type: 'topic_wrong', label: 'Wrong topic' },
  { type: 'topic_missing', label: 'Missing topic' },
  { type: 'recommendation_irrelevant', label: 'Rec irrelevant' },
  { type: 'data_error', label: 'Data error' },
];

export function FeedbackControls({
  leadId,
  onSubmitted,
}: {
  leadId: string;
  onSubmitted?: () => void;
}): JSX.Element {
  const [pending, setPending] = useState<FeedbackType | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (type: FeedbackType): Promise<void> => {
    setPending(type);
    setError(null);
    setDone(null);
    try {
      await api.submitFeedback(leadId, type);
      setDone(type);
      onSubmitted?.();
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to submit feedback',
      );
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="feedback-controls">
      <span className="feedback-title">Corrections:</span>
      {ACTIONS.map((a) => (
        <button
          key={a.type}
          type="button"
          className="btn btn-small"
          disabled={pending !== null}
          onClick={() => submit(a.type)}
        >
          {pending === a.type ? '…' : a.label}
        </button>
      ))}
      {done && <span className="feedback-done">Recorded ✓</span>}
      {error && <span className="feedback-error">{error}</span>}
    </div>
  );
}
